import React, { useCallback } from 'react';
import {
  Autocomplete,
  CircularProgress,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useDebounceFn } from '../../../hooks/useDebounce';

export interface AppAutocompleteProps<T> {
  name?: string;
  options: T[];
  label?: string;
  sx?: SxProps<Theme>;
  isError?: boolean;
  defaultValue?: T | null | undefined;
  handleChange?: (e: React.SyntheticEvent, value: T) => void;
  getOptionDisabled?: (option: T) => boolean;
  getOptionLabel?: (option: T | string) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  mRef?: React.Ref<HTMLDivElement>;

  isRequired?: boolean;
  disabled?: boolean;
  isFreeSolo?: boolean;
  searchOptions?(value: string): void;
  onSelect?(
    value: string | T | null | readonly (string | string[] | T | T[])[]
  ): void;
  onFocus?(): void;
  isLoading?: boolean;
  noOptionsText?: string;
  placeholder?: string;
  readOnly?: boolean;
  groupBy?(option: T): string;
  debounceDelay?: number;
  autoSelect?: boolean;
  errorMessage?: string;
}

export function AppAutocomplete<T>({
  options,
  label,
  sx,
  isFreeSolo,
  isLoading,
  isRequired,
  isError,
  handleChange,
  getOptionDisabled,
  onSelect,
  searchOptions,
  placeholder,
  errorMessage,
  debounceDelay = 250,
  mRef,
  ...props
}: AppAutocompleteProps<T>): JSX.Element {
  const debounce = useDebounceFn(debounceDelay);

  const onInputChange = useCallback(
    (event, value) => {
      if (!event || event?.type === 'click') {
        return;
      }

      debounce(() => {
        searchOptions?.(value);
      });
    },
    [debounce, searchOptions]
  );

  const onChange = useCallback(
    (event, value) => {
      if (onSelect) {
        onSelect(value);
      }

      if (handleChange) {
        handleChange(event, value);
      }
    },
    [handleChange, onSelect]
  );

  return (
    <Autocomplete
      ref={mRef}
      {...props}
      freeSolo={isFreeSolo}
      loading={isLoading}
      sx={sx}
      ListboxProps={{
        style: {
          maxHeight: '200px',
          padding: 0,
          boxShadow:
            '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30)',
        },
      }}
      clearIcon={<HighlightOffIcon />}
      onChange={onChange}
      onInputChange={onInputChange}
      getOptionDisabled={getOptionDisabled}
      filterOptions={(filterOptions) => filterOptions}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          label={isRequired ? `${label}*` : label}
          error={!!isError}
          placeholder={placeholder}
          helperText={errorMessage}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    sx={{ marginRight: 1 }}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}
